import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /**
   * Optional label rendered as the kicker subline, e.g. the module name
   * the boundary is wrapping. Falls back to the generic copy.
   */
  scope?: string;
}

interface State {
  error: Error | null;
}

/**
 * Class-component error boundary — React's official API still requires a
 * class for `getDerivedStateFromError` / `componentDidCatch`. Wraps any
 * subtree (the outer Shell or an individual lazy module) so a single
 * broken bundle (e.g. `/aiaiai` throwing on import) does NOT blank the
 * whole SPA.
 *
 * Visual language mirrors ForbiddenPage / PlaceholderPage (paper tone,
 * mono kicker, big serif headline, muted body, accent underline link).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Surface to the console so the hub-react devtools / Sentry shim (if
    // wired later) can see the stack. Intentionally not throwing further.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.scope ?? 'app', error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const kicker = this.props.scope
      ? `§ error · ${this.props.scope}`
      : '§ error · something went wrong';

    return (
      <div
        style={{
          position: 'relative',
          padding: 40,
          display: 'grid',
          placeItems: 'center',
          minHeight: 'min(60dvh, 480px)',
        }}
      >
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 520,
            background: 'var(--bg-card, var(--bg))',
            border: '1px solid var(--border)',
            borderRadius: 'var(--hm-radius, 12px)',
            padding: '28px 32px',
            boxShadow:
              'var(--q-shadow-2, 0 24px 56px -32px color-mix(in oklab, var(--c-admin) 40%, transparent))',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--hm-mono)',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--c-admin)',
              fontWeight: 600,
            }}
          >
            {kicker}
          </span>
          <h1
            style={{
              fontFamily: 'var(--hm-font)',
              fontSize: 36,
              fontWeight: 500,
              letterSpacing: '-0.025em',
              margin: '8px 0 12px',
              lineHeight: 1.1,
              color: 'var(--text)',
            }}
          >
            Something went wrong.
          </h1>
          <p
            style={{
              color: 'var(--text-muted)',
              fontSize: 14,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            This panel hit an error and stopped rendering. The rest of the
            hub is still alive. Try again, or reload the module if it
            keeps failing.
          </p>
          {error.message ? (
            <pre
              style={{
                marginTop: 16,
                padding: '10px 12px',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--hm-radius, 8px)',
                fontFamily: 'var(--hm-mono)',
                fontSize: 11,
                lineHeight: 1.5,
                color: 'var(--text-muted)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 140,
                overflow: 'auto',
              }}
            >
              {error.message}
            </pre>
          ) : null}
          <div
            style={{
              marginTop: 20,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                fontFamily: 'var(--hm-mono)',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: 'var(--hm-radius-pill, 999px)',
                // The new interface prints this as the primary action: a solid
                // house fill with the ink that sits on it.
                color: 'var(--q-on-accent, var(--c-ai))',
                background:
                  'var(--q-flat-accent, color-mix(in oklab, var(--c-ai) 16%, transparent))',
                border:
                  '1px solid var(--q-still, color-mix(in oklab, var(--c-ai) 30%, transparent))',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                fontFamily: 'var(--hm-mono)',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: 'var(--hm-radius-pill, 999px)',
                color: 'var(--text-muted)',
                background: 'transparent',
                border: '1px solid var(--border)',
              }}
            >
              Reload module
            </button>
          </div>
        </div>
      </div>
    );
  }
}
